import { RefreshCw } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipPopup, TooltipTrigger } from "@/components/ui/tooltip";
import { useLocale } from "@/hooks/use-locale";
import { useRelativeTime } from "@/hooks/use-relative-time";
import { NAMESPACES } from "@/i18n/constants";
import { cn } from "@/utils/class-name";
import { useDataViewerStore } from "../data-viewer-provider";

const SPIN_ANIM_DURATION_MS = 500;

const TIME_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
};

export function LastUpdatedBar() {
    const { t } = useTranslation(NAMESPACES.FEATURES.DATA_VIEWER, { keyPrefix: "time-window.last-updated-bar" });
    const { t: tCommon } = useTranslation(NAMESPACES.COMMON);
    const locale = useLocale();
    const endMs = useDataViewerStore((state) => state.endMs);
    const advanceWindowToNow = useDataViewerStore((state) => state.advanceWindowToNow);
    const { timeStringWithAgoSuffix } = useRelativeTime(endMs);
    const [isSpinning, setIsSpinning] = useState(false);

    const formattedTime = useMemo(
        () => new Date(endMs).toLocaleTimeString(locale, TIME_FORMAT_OPTIONS),
        [endMs, locale],
    );

    useEffect(() => {
        if (!isSpinning) return undefined;
        const id = setTimeout(() => setIsSpinning(false), SPIN_ANIM_DURATION_MS);
        return () => {
            clearTimeout(id);
        };
    }, [isSpinning]);

    const handleRefresh = () => {
        if (isSpinning) return;
        setIsSpinning(true);
        advanceWindowToNow();
    };

    return (
        <div className={"flex items-center justify-end gap-0.5 text-muted-foreground text-xs"}>
            <Tooltip disableHoverablePopup>
                <TooltipTrigger delay={100}>
                    <span>{t("label", { relativeTime: timeStringWithAgoSuffix })}</span>
                </TooltipTrigger>
                <TooltipPopup>{formattedTime}</TooltipPopup>
            </Tooltip>

            <Button
                aria-label={tCommon("actions.refresh")}
                onClick={handleRefresh}
                size={"icon-sm"}
                tooltip={tCommon("actions.refresh")}
                variant={"ghost"}
            >
                <RefreshCw className={cn("size-3.5", { "animate-spin": isSpinning })} />
            </Button>
        </div>
    );
}
